import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Music, 
  BookOpen, 
  Dumbbell, 
  FileText, 
  Mic2, 
  Drum, 
  Crown, 
  Menu, 
  X,
  User,
  LogOut,
  Settings,
  Globe,
  Circle,
  Timer,
  GraduationCap,
  GitBranch,
  Activity,
  Library
} from 'lucide-react';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, isPremium, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/skills', label: t('nav.skills'), icon: GitBranch },
    { path: '/progress', label: t('nav.progress'), icon: Activity },
    { path: '/chords', label: t('nav.chords'), icon: Music },
    { path: '/exercises', label: t('nav.exercises'), icon: Dumbbell },
    { path: '/etudes', label: t('nav.etudes'), icon: Library },
    { path: '/theory', label: t('nav.theory'), icon: BookOpen },
    { path: '/songs', label: t('nav.songs'), icon: FileText },
    { path: '/tuner', label: t('nav.tuner'), icon: Mic2 },
    { path: '/drums', label: t('nav.drums'), icon: Drum },
    { path: '/metronome', label: t('nav.metronome'), icon: Timer },
    { path: '/recorder', label: t('nav.recorder'), icon: Circle },
    { path: '/tutor', label: t('nav.tutor'), icon: GraduationCap },
  ];

  const toggleLanguage = () => {
    const newLang = i18n.language === 'it' ? 'en' : 'it';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center group-hover:scale-105 transition-transform">
              <Music className="w-6 h-6 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white hidden sm:block">FretFlow</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-amber/10 text-amber'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="text-zinc-400 hover:text-white"
            >
              <Globe className="w-4 h-4 mr-1" />
              {i18n.language.toUpperCase()}
            </Button>

            {/* Premium Badge/Button */}
            {isAuthenticated && !isPremium && (
              <Link to="/premium">
                <Button 
                  size="sm" 
                  data-testid="premium-btn"
                  className="bg-gradient-to-r from-amber to-orange-500 text-black font-semibold hover:opacity-90"
                >
                  <Crown className="w-4 h-4 mr-1" />
                  Premium
                </Button>
              </Link>
            )}

            {isPremium && (
              <div className="premium-badge flex items-center gap-1">
                <Crown className="w-3 h-3" />
                Premium
              </div>
            )}

            {/* Auth Buttons / User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="user-menu-btn" className="text-zinc-400 hover:text-white">
                    <User className="w-4 h-4 mr-2" />
                    {user?.username}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#121212] border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center cursor-pointer">
                      <Settings className="w-4 h-4 mr-2" />
                      {t('nav.dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/recorder" className="flex items-center cursor-pointer">
                      <Mic2 className="w-4 h-4 mr-2" />
                      {t('nav.recorder')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={logout} 
                    data-testid="logout-btn"
                    className="text-red-400 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button size="sm" data-testid="login-btn" className="bg-white text-black hover:bg-zinc-200">
                  {t('nav.login')}
                </Button>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-zinc-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/5">
            <div className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-amber/10 text-amber'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
